import type { Task } from './models'
import { daysBetween, isSameDay } from '../date/calendar'
import { wakeStart } from '../recurrence/wakeStart'

/** Was die Zählregel von der Zeile VOR der Änderung liest. */
export type CountSource = Pick<Task, 'id' | 'status' | 'startDate' | 'recurrence'>
  & Partial<Pick<Task, 'pausedAt' | 'completedCount' | 'postponedCount'>>

export type CounterFields = Partial<Pick<Task, 'completedCount' | 'postponedCount'>>

export interface TaskCounter {
  /**
   * Die Zähler-Felder, die mit `changes` zu schreiben sind — leer, wenn nichts zählt. Bringt die
   * Änderung einen Zähler selbst mit (Import, Nachfolger), bleibt der unberührt.
   */
  count(before: CountSource, changes: Partial<Task>, now?: Date): CounterFields
}

/**
 * **Erledigt-Zähler** und **Vertagen** (#198, CONTEXT.md → Zähler). Eine je Datenquelle: Sie merkt
 * sich, wer in dieser Sitzung abgehakt bzw. wieder geöffnet wurde — **Hin und her gleicht sich aus**,
 * dieselbe Grenze wie beim Nachfolger. Das deckt jeden Rückweg (Haken zurück, ⌘Z, „Rückgängig“ auf
 * Geschafft), denn alle schreiben nur den Status zurück. Sync und Import schreiben an `updateTask`
 * vorbei und zählen nicht.
 */
export function createTaskCounter(): TaskCounter {
  const completedThisSession = new Set<string>()
  // Wieder geöffnet, ohne dass es ein Abhaken dieser Sitzung zurücknahm: Der Stand blieb, also zählt
  // auch das Zurücknehmen des Öffnens nicht.
  const reopenedThisSession = new Set<string>()
  return {
    count(before, changes, now = new Date()) {
      const out: CounterFields = {}
      if (!('completedCount' in changes) && changes.status !== undefined && changes.status !== before.status) {
        if (changes.status === 'completed') {
          if (!reopenedThisSession.delete(before.id)) {
            out.completedCount = (before.completedCount ?? 0) + 1
            completedThisSession.add(before.id)
          }
        } else if (before.status === 'completed') {
          if (completedThisSession.delete(before.id)) out.completedCount = Math.max(0, (before.completedCount ?? 0) - 1)
          else reopenedThisSession.add(before.id)
        }
      }
      if (!('postponedCount' in changes) && postpones(before, changes, now)) {
        out.postponedCount = (before.postponedCount ?? 0) + 1
      }
      return out
    },
  }
}

/**
 * **Vertagen:** Das Wann einer offenen Aufgabe rückt auf einen späteren Tag — und der Nutzer hat ihn
 * gewählt. Setzt die Regel den Tag (eine neue Wiederholung im selben Zug, oder Aufwecken holt ihren
 * Termin nach), zählt es nicht. Das Aufwecken sieht aus wie „Morgen“ aus dem Reaktivieren-Menü; es
 * zeigt sich daran, dass genau der Tag kommt, den `wakeStart` ohnehin gesetzt hätte.
 */
function postpones(before: CountSource, changes: Partial<Task>, now: Date): boolean {
  const from = before.startDate
  const to = changes.startDate
  if (!from || !to) return false
  if (before.status === 'completed' || (changes.status ?? before.status) === 'completed') return false
  if (daysBetween(new Date(from), new Date(to)) <= 0) return false
  // Das Formular schickt die Regel bei jedem Speichern mit — nur eine NEUE setzt den Tag.
  if (changes.recurrence != null && JSON.stringify(changes.recurrence) !== JSON.stringify(before.recurrence ?? null)) return false
  if (changes.pausedAt === null) {
    const ruleDay = wakeStart({ recurrence: before.recurrence, startDate: new Date(from) }, now).startDate
    if (ruleDay && isSameDay(ruleDay, new Date(to))) return false
  }
  return true
}
